/**
 * Saving stock documents (.docs/023).
 *
 * The step-1 helper that kept retry keys per request is gone: every stock save
 * now goes through the outbox, which makes a key once, keeps it with the work
 * and sends it again by itself.
 */
import { UNREACHABLE_MESSAGE } from '@/api/client';
import { saveWork, takeBack } from '@/offline/engine';
import type { Kind, NewOperation } from '@/offline/outbox';
import { announceSavedOffline } from '@/stores/savedOffline';

/**
 * A quantity less what has already gone, as the exact decimal string the API
 * wants. Worked in ten-thousandths, the API's own precision, so 0.3 − 0.1 is
 * 0.2 and not 0.19999999999999998.
 */
export function quantityLeft(total: string, gone: readonly string[]): string {
  const scale = (q: string) => Math.round(Number(q) * 10_000);
  const left = scale(total) - gone.reduce((sum, q) => sum + scale(q), 0);
  return String(left / 10_000);
}

/** What saving a stock document through the outbox came to. */
export interface DocumentSaved {
  documentNo: string;
  /** No answer yet: safe on this PC, and sent by itself later. */
  waiting: boolean;
  /** Made, but its post was refused: a draft to post from the list. */
  warning?: string;
}

/** Said when work is kept on this PC to be sent later. */
export const KEPT_ON_THIS_PC = 'Saved on this PC. It will be sent when the connection is back.';

/**
 * Saves one stock document, and its post if asked, through the outbox
 * (.docs/023): answered at once with a line, kept on this PC without one,
 * under a retry key made once. A refusal while the dialog is open is taken
 * back out and thrown, for the dialog to show. For the quick and bulk actions;
 * the full screens build their own batches.
 */
export async function saveDocument(input: {
  kind: 'inward' | 'consumption' | 'transfer' | 'adjustment' | 'demand';
  path: string;
  body: object;
  post: boolean;
  describes: string;
  screen: string;
  form?: unknown;
  /** Kept for later without the dialog saying so: a bulk action says it once, for all. */
  quiet?: boolean;
  /** Saved ahead of it, in the same batch: a supplier typed in as new, say. */
  before?: readonly NewOperation[];
}): Promise<DocumentSaved> {
  const before = input.before ?? [];
  const batch: NewOperation[] = [
    ...before,
    {
      kind: `${input.kind}.create` as Kind,
      method: 'POST',
      path: input.path,
      body: input.body,
      describes: input.describes,
      screen: input.screen,
      ...(input.form === undefined ? {} : { form: input.form }),
    },
  ];
  if (input.post) {
    batch.push({
      kind: `${input.kind}.post` as Kind,
      method: 'POST',
      path: `${input.path}/{dep}/post`,
      after: before.length,
      describes: `Posting: ${input.describes}`,
      screen: input.screen,
    });
  }

  const saved = await saveWork(batch);
  const docOps = saved.ops.slice(before.length);
  const made = docOps[0]?.result as { documentNo?: string } | undefined;
  const documentNo = made?.documentNo ?? '';
  if (saved.outcome === 'sent') return { documentNo, waiting: false };
  if (saved.outcome === 'waiting') {
    if (!input.quiet) void announceSavedOffline({ what: input.describes, closeLabel: 'OK', note: 'It gets its document number when it is sent.' });
    return { documentNo, waiting: true };
  }

  for (const op of saved.ops) if (op.state !== 'sent') await takeBack(op.id).catch(() => undefined);
  const reason = saved.ops.find((op) => op.state === 'refused')?.lastError ?? UNREACHABLE_MESSAGE;
  if (docOps[0]?.state === 'sent') {
    return { documentNo, waiting: false, warning: `${documentNo} was saved as a draft but not posted: ${reason}. Post it from the list.` };
  }
  throw new Error(reason);
}
